/**
 * <mw-header> — mega menu disclosure, mobile drawer and header height variables.
 *
 * - Mega menus: `[data-mega]` items with a `[data-mega-trigger]` button that controls a panel
 *   (`aria-controls`). Opens on hover (mouse), click or ArrowDown; closes on Escape, outside
 *   interaction or pointer leave.
 * - Drawer: `[data-drawer-open]` opens the native modal <dialog> named by its `aria-controls`.
 *   Opening one of its groups loads that group's thumbnails and keeps the group title in view.
 * - Publishes `--header-height` / `--header-group-height` on <body> for Horizon consumers.
 * - Sets `--account-dialog-top` on <shopify-account> when its dialog opens.
 */
const DESKTOP = '(min-width: 990px)';

class MwHeader extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ResizeObserver | undefined} */
  #resize;
  #closeTimer = 0;
  /** @type {HTMLElement | null} */
  #openItem = null;
  #openedByHover = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };

    for (const item of this.querySelectorAll('[data-mega]')) {
      if (!(item instanceof HTMLElement)) continue;
      const trigger = item.querySelector('[data-mega-trigger]');

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && this.#openItem !== item) this.#open(item, true);
        clearTimeout(this.#closeTimer);
      }, options);
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.#scheduleClose();
      }, options);
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.#open(item, false);
        item.querySelector('.mw-mega a')?.focus();
      }, options);
      trigger?.addEventListener('click', () => {
        // A hover already opened the panel before the first click; keep it open.
        if (this.#openItem === item && !this.#openedByHover) this.#close();
        else this.#open(item, false);
      }, options);
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#openItem) return;
      const trigger = this.#openItem.querySelector('[data-mega-trigger]');
      this.#close();
      if (trigger instanceof HTMLElement) trigger.focus();
    }, options);
    document.addEventListener('pointerdown', (event) => this.#closeIfOutside(event.target), options);
    document.addEventListener('focusin', (event) => this.#closeIfOutside(event.target), options);

    this.#setupDrawer(options);

    // Anchor Shopify's account dialog below its button, as Horizon's header.js does.
    this.addEventListener('open', (event) => {
      const { target } = event;
      if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;
      const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
      target.style.setProperty('--account-dialog-top', `${bottom}px`);
    }, { ...options, capture: true });

    this.#resize = new ResizeObserver(() => this.#publishHeights());
    this.#resize.observe(this);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
    clearTimeout(this.#closeTimer);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} byHover
   */
  #open(item, byHover) {
    if (!matchMedia(DESKTOP).matches) return;
    clearTimeout(this.#closeTimer);
    if (this.#openItem !== item) this.#close();
    this.#openItem = item;
    this.#openedByHover = byHover;
    this.#setExpanded(item, true);
  }

  #close() {
    clearTimeout(this.#closeTimer);
    if (this.#openItem) this.#setExpanded(this.#openItem, false);
    this.#openItem = null;
  }

  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      if (!this.#openItem?.contains(document.activeElement)) this.#close();
    }, 160);
  }

  /** @param {EventTarget | null} target */
  #closeIfOutside(target) {
    if (this.#openItem && !(target instanceof Node && this.#openItem.contains(target))) this.#close();
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = trigger && document.getElementById(trigger.getAttribute('aria-controls') ?? '');
    trigger?.setAttribute('aria-expanded', String(expanded));
    if (panel) panel.hidden = !expanded;
  }

  /** @param {AddEventListenerOptions} options */
  #setupDrawer(options) {
    const opener = this.querySelector('[data-drawer-open]');
    const drawer = opener && document.getElementById(opener.getAttribute('aria-controls') ?? '');
    if (!(opener instanceof HTMLElement) || !(drawer instanceof HTMLDialogElement)) return;

    opener.addEventListener('click', () => {
      drawer.showModal();
      opener.setAttribute('aria-expanded', 'true');
    }, options);
    drawer.querySelector('[data-drawer-close]')?.addEventListener('click', () => drawer.close(), options);
    // Clicks on the backdrop land on the dialog element itself.
    drawer.addEventListener('click', (event) => event.target === drawer && drawer.close(), options);
    drawer.addEventListener('close', () => {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus();
    }, options);
    // An opened group loads all its thumbnails at once: lazy images outside the drawer's visible
    // area otherwise stay empty squares until scrolled to. Opening a group also closes the one
    // above it (exclusive accordion), which can scroll the new title out of view: bring it back.
    drawer.addEventListener('toggle', (event) => {
      const group = event.target;
      if (!(group instanceof HTMLDetailsElement) || !group.open) return;
      for (const image of group.querySelectorAll('img[loading="lazy"]')) {
        if (image instanceof HTMLImageElement) image.loading = 'eager';
      }
      const summary = group.querySelector('summary');
      const head = drawer.querySelector('.mw-drawer__head');
      const top = head ? head.getBoundingClientRect().bottom : drawer.getBoundingClientRect().top;
      const offset = summary ? summary.getBoundingClientRect().top - top : 0;
      if (offset < 0) drawer.scrollBy({ top: offset });
    }, { ...options, capture: true });
    matchMedia(DESKTOP).addEventListener('change', (event) => event.matches && drawer.open && drawer.close(), options);
  }

  #publishHeights() {
    const group = this.closest('#header-group');
    let groupHeight = 0;
    for (const section of group?.children ?? []) {
      if (section instanceof HTMLElement) groupHeight += section.offsetHeight;
    }
    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight || this.offsetHeight}px`);
  }
}

if (!customElements.get('mw-header')) customElements.define('mw-header', MwHeader);
